import * as React from 'react';
import FocusLock, { MoveFocusInside } from 'react-focus-lock';
import { ClickAwayListener, withStyles } from '@material-ui/core';
import LookupPanel from 'components/common/LookupPanel';
import ProductReceiptsSearch from './ProductReceiptsSearch';
import IProductReceiptLookupPanelProps, { IProductReceiptLookupPanelHandle } from './ProductReceiptsLookupPanel.properties';
import { IProductReceiptSearchHandle } from './ProductReceiptsSearch/ProductReceiptsSearch.properties';

import { useRetrieveProductReceiptContext } from 'api/productReceipts/productReceipts';
import { usePRContextSelector, usePRDispatch } from '../ProductReceipts.context';
import { showSnackBar } from 'components/common/SnackBars/SnackBar.hooks';
import ParkedReceipts from './ParkedReceipts';
import { ProductReceiptsActionTypes } from '../ProductReceipts.actions';
import Scrollbars from 'react-custom-scrollbars';
import inlineStyles from './ProductReceiptsLookupPanel.styles';

function ProductReceiptsLookupPanel(props: IProductReceiptLookupPanelProps, ref: React.Ref<IProductReceiptLookupPanelHandle>): JSX.Element {
    const {
        open, classes, loading, parkedReceiptsItems, parkedReceiptsItemsLoading,
        onOpenChanged: onToggleOpen, onApplyProductReceiptContext
    }: IProductReceiptLookupPanelProps = props;

    const ProductReceiptId = usePRContextSelector<'ProductReceipt'>((state) => state.ProductReceipt);
    const ProductReceiptContextInline = usePRContextSelector<'ProductReceiptContext'>((state) => state.ProductReceiptContext);

    const retrieveProductReceiptContextMutation = useRetrieveProductReceiptContext();

    const [onFirstTimeLoaded, setOnFirstTimeLoad] = React.useState<boolean>(true);

    const productReceiptSearchRef = React.useRef<IProductReceiptSearchHandle>();
    const contextDispatch = usePRDispatch();

    React.useEffect(
        () => {
            if (ProductReceiptId !== undefined) {
                if (ProductReceiptId > 0 && onFirstTimeLoaded) {
                    loadReceipt(ProductReceiptId);
                } else {
                    retrieveProductReceiptContextMutation.mutateAsync({ ProductReceiptId: ProductReceiptId }).then((response) => {
                        contextDispatch({
                            [ProductReceiptsActionTypes.setProductReceiptContext]: response?.ProductReceiptContext?.inlineObject,
                        });
                    });
                }
                setOnFirstTimeLoad(false);
            }
        },
        [ProductReceiptId]
    );

    async function handleRecordChange(recordId: any): Promise<void> {
        await loadReceipt(recordId);
    }

    async function loadReceipt(recordId: any): Promise<void> {
        const response = await retrieveProductReceiptContextMutation.mutateAsync({ ProductReceiptId: recordId });
        if (response?.Status) {
            contextDispatch({
                [ProductReceiptsActionTypes.setProductReceipt]: response.ProductReceiptContext?.inlineObject?.ProductReceiptId,
            });
            contextDispatch({
                [ProductReceiptsActionTypes.setProductReceiptContext]: response.ProductReceiptContext?.inlineObject,
            });
            onToggleOpen(!response?.Status);
        }
    }

    React.useImperativeHandle(
        ref,
        () => ({
            focusWarehouse(): void {
                productReceiptSearchRef.current?.focusOnWarehouseField();
            }
        })
    );

    async function handleApply(values: any): Promise<void> {
        if (onApplyProductReceiptContext) {
            const response = await onApplyProductReceiptContext(values);

            if (!response?.Status) {
                productReceiptSearchRef.current?.focusOnWarehouseField();
            }
            if (response) {
                contextDispatch({
                    [ProductReceiptsActionTypes.setProductReceiptContext]: response.ProductReceiptContext?.inlineObject,
                });
            }
            onToggleOpen(!response?.Status);
        }
    }

    const warnChanges = () => {
        if (productReceiptSearchRef.current?.isDirty()) {
            showSnackBar({ variant: 'warning', message: 'Please apply or undo changes.' });
        }
    };

    const handleOnClickAway = (e) => {
        const dialogElement = document.querySelector('.MuiDialog-container');
        if (open && !dialogElement?.contains(e.target)) { // check if click is not on dialog box otherwise show warning
            warnChanges();
        }
    };

    const formData = React.useMemo(
        () => (ProductReceiptContextInline),
        [ProductReceiptContextInline]
    );

    return (

        <ClickAwayListener onClickAway={handleOnClickAway} >

            <Scrollbars className={open ? classes.scrollBarStyleOpened : classes.scrollBarStyleClosed}
            >
                <LookupPanel open={open} setOpen={onToggleOpen}>
                    <FocusLock autoFocus={open}>
                        <MoveFocusInside>
                            <ProductReceiptsSearch
                                innerRef={productReceiptSearchRef}
                                loading={loading}
                                formData={formData}
                                onApply={handleApply}
                                onUndo={() => undefined}
                            />
                        </MoveFocusInside>
                    </FocusLock>
                    <ParkedReceipts
                        listItems={parkedReceiptsItems}
                        isLoading={loading || parkedReceiptsItemsLoading}
                        disabled={ProductReceiptId > 0}
                        keyField={'ProductReceiptId'}
                        fieldsToDisplay={['ProductReceiptId', 'DateTime', 'SupplierIdLabel', null]}
                        handleRecordChange={handleRecordChange}
                    />
                </LookupPanel>
            </Scrollbars>

        </ClickAwayListener>
    );
}

export default withStyles(inlineStyles, { index: 1 })(React.memo(React.forwardRef(ProductReceiptsLookupPanel)));
